import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { AuthContext } from './AuthContext';
import api from '../utils/axiosConfig';

export const AppStateContext = createContext();

export const AppStateProvider = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Simple state - no complex interdependencies
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pure utility functions - no state dependencies
  const getUrlParams = () => ({
    orgId: searchParams.get('org'),
    eventId: searchParams.get('event')
  });

  const updateUrlParams = (orgId, eventId = null) => {
    const params = new URLSearchParams(searchParams);
      console.log("Updating URL params:", { orgId, eventId });
    if (orgId) params.set('org', orgId);
    else params.delete('org');
    
    if (eventId) params.set('event', eventId);
    else params.delete('event');
    
    setSearchParams(params);
  };

  // Simple data fetchers - single responsibility
  const fetchOrganizations = async () => {
    if (!isAuthenticated) return [];
    
    try {
      const response = await api.get('/organizations');
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
  };

  const fetchEvents = async (orgId) => {
    if (!isAuthenticated || !orgId) return [];
    
    try {
      const response = await api.get(`/organizations/${orgId}/events`);
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  };

  // Pure selection functions
  const selectOrganization = (organizations, orgId) => {
    if (!orgId) return organizations[0] || null;
    return organizations.find(org => org.id === orgId) || organizations[0] || null;
  };

  const selectEvent = (events, eventId) => {
    if (!eventId) return events[0] || null;
    return events.find(event => event.id === eventId) || events[0] || null;
  };

  // Main initialization function - orchestrates everything
  const initializeAppState = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setEvents([]);
      setCurrentEvent(null);
      updateUrlParams(null, null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Get organizations
      const orgs = await fetchOrganizations();
      setOrganizations(orgs);

      if (orgs.length === 0) {
        setCurrentOrganization(null);
        setEvents([]);
        setCurrentEvent(null);
        setLoading(false);
        return;
      }

      // Step 2: Select organization (from URL or default to first)
      const { orgId, eventId } = getUrlParams();
      const selectedOrg = selectOrganization(orgs, orgId);
      setCurrentOrganization(selectedOrg);

      // Step 3: Get events for selected organization
      const orgEvents = await fetchEvents(selectedOrg.id);
      setEvents(orgEvents);

      // Step 4: Select event (from URL or default to first)
      const selectedEvent = selectEvent(orgEvents, eventId);
      setCurrentEvent(selectedEvent);

      // Step 5: Update URL to reflect current state
      updateUrlParams(selectedOrg.id, selectedEvent?.id);

    } catch (error) {
      setError('Failed to load data');
      console.error('Initialization error:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, searchParams]);


  const switchOrganization = useCallback(async (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) return;

    setCurrentOrganization(org);
    setCurrentEvent(null);
    setLoading(true);

    try {
      const orgEvents = await fetchEvents(orgId);
      setEvents(orgEvents);
      
      const selectedEvent = selectEvent(orgEvents, null); // Default to first event
      setCurrentEvent(selectedEvent);
      
      updateUrlParams(orgId, selectedEvent?.id);
    } catch (error) {
      setError('Failed to switch organization');
    } finally {
      setLoading(false);
    }
  }, [organizations]);

  const switchEvent = useCallback((eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event || !currentOrganization) return false;

    setCurrentEvent(event);
    updateUrlParams(currentOrganization.id, eventId);
    return true;
  }, [events, currentOrganization]);

  // Simplified refresh - just re-run initialization
  const refreshData = useCallback(async () => {
    await initializeAppState();
  }, [initializeAppState]);

  // New event creation helper
  const addNewEvent = useCallback(async (eventId) => {
    if (!currentOrganization) return false;

    setLoading(true);
    try {
      // Refresh events for current organization
      const updatedEvents = await fetchEvents(currentOrganization.id);
      setEvents(updatedEvents);
      
      // Find and select the new event
      const newEvent = updatedEvents.find(e => e.id === eventId);
      if (newEvent) {
        console.log("found and switching to new event:", newEvent);
        setCurrentEvent(newEvent);
        updateUrlParams(currentOrganization.id, newEvent.id);
        return true;
      }
      console.warn('New event not found in updated events');
      return false;
    } catch (error) {
      setError('Failed to add new event');
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  // Utility functions
  const getEventById = useCallback((eventId) => {
    return events.find(event => event.id === eventId) || null;
  }, [events]);

  const hasRole = useCallback((allowedRoles) => {
    if (!currentOrganization) return false;
    return allowedRoles.includes(currentOrganization.role);
  }, [currentOrganization]);

  // Initialize on mount and auth changes
  useEffect(() => {
    initializeAppState();
  }, [initializeAppState]);

  // Context value
  const value = {
    // State
    organizations,
    currentOrganization,
    events,
    currentEvent,
    loading,
    error,
    
    // Actions
    switchOrganization,
    switchEvent,
    addNewEvent,
    refreshData,
    
    // Utilities
    getEventById,
    hasRole,
    currentRole: currentOrganization?.role || null
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};